// Worker protocol + chunk (de)serialization (ENG-8 / ARCH §3.5). PURE and
// isomorphic: the message shapes and the payload round-trip are used by the gen
// worker, the client, and (later) the server/save layer. Payloads are flat typed
// arrays so they transfer zero-copy. Every message carries jobId + rev; the
// consumer drops results whose rev is stale (E-3).
import { Chunk } from '../engine/chunk';
import { Section, SECTION_VOLUME, DEFAULT_RANGE, type YRange } from '../engine/section';
import type { GeneratorIds, GeneratorBiomes, StructureStates } from '../gen/generator';
import type { IntermediateCity } from '../atlas/intermediate';
import type { AtlasElevation } from '../atlas/elevation';
import type { EarthWorld } from '../earth/earthgen';
import type { OreConfig, Vegetation } from '../registry/schemas';
import type { RenderTable } from '../mesh/atlasmap';
import type { GeoBuffers } from '../mesh/mesher';

export interface GenInitMsg {
  type: 'init';
  seed: number | string;
  flat: boolean;
  /** Which dimension the worker generates (DIM-1); absent ⇒ overworld. */
  dimension?: 'overworld' | 'nether' | 'end';
  /** Vertical extent of the generated dimension (TAL-1); absent ⇒ default range. */
  range?: YRange;
  /** Atlas city model (ATL-1); present ⇒ the worker voxelizes the city. */
  atlas?: IntermediateCity;
  /** Real elevation for an Atlas world (M30/ELV-1); absent ⇒ flat Atlas ground. */
  atlasElevation?: AtlasElevation;
  /** Earth world (ERT-2); present ⇒ the worker voxelizes decoded TIL tiles. */
  earth?: EarthWorld;
  /** Bounded child world (DOR-3, M41); present ⇒ the worker walls off the edges. */
  bounds?: { half: number };
  ids: GeneratorIds;
  blockStates: Record<string, number>;
  biomes: GeneratorBiomes | undefined;
  ores: readonly OreConfig[] | undefined;
  vegetation: Vegetation | undefined;
  opacityByState: Uint8Array;
  emissionByState: Uint8Array;
  structureStates: StructureStates | undefined;
}

export interface GenRequestMsg {
  type: 'gen';
  jobId: number;
  rev: number;
  cx: number;
  cz: number;
}

/** A chunk flattened for transfer (TAL-2): the PRESENT sections only — no dense
 *  ~672-slot null-padded arrays travel. `sections` is `{sy, states}[]` (sy = the
 *  section's array index in its column), with `skyLight`/`blockLight` as parallel
 *  sparse lists in the same order. The two heightmaps travel dense. Light travels
 *  with the chunk so the mesh worker samples real sky/block light — including a
 *  neighbour's light for border faces (§5.2). */
export interface ChunkPayload {
  cx: number;
  cz: number;
  /** Vertical extent this chunk was built for (TAL-1); its column length =
   *  `range.sectionsPerColumn`. Absent on legacy payloads ⇒ default range. */
  range?: YRange;
  sections: { sy: number; states: Uint16Array }[];
  /** Parallel to `sections`: the two 0..15 light channels per present section. */
  skyLight: Uint8Array[];
  blockLight: Uint8Array[];
  worldSurface: Int16Array;
  motionBlocking: Int16Array;
}

export interface GenResultMsg extends ChunkPayload {
  type: 'result';
  jobId: number;
  rev: number;
}

/** Main thread → gen worker. */
export type WorkerInbound = GenInitMsg | GenRequestMsg;
/** Gen worker → main thread. */
export type WorkerOutbound = GenResultMsg;

// ── Mesh worker (ENG-8 / §3.5). Stateless per job: each request carries the
//    chunk plus its 4 horizontal neighbors as disposable copies, so the worker
//    needs no synced cache. `rev` lets the client drop results made stale by an
//    edit that happened after the request (E-3). ──────────────────────────────

export interface MeshInitMsg {
  type: 'meshInit';
  table: RenderTable;
}

export interface MeshRequestMsg {
  type: 'mesh';
  jobId: number;
  rev: number;
  cx: number;
  cz: number;
  self: ChunkPayload;
  /** Horizontal neighbors in order [+x, -x, +z, -z]; null if unavailable. */
  neighbors: (ChunkPayload | null)[];
  /** Surface-only LOD depth (perf, flight); 0/absent ⇒ full-detail mesh. */
  lod?: number;
}

export interface MeshResultMsg {
  type: 'meshResult';
  jobId: number;
  rev: number;
  cx: number;
  cz: number;
  originX: number;
  originZ: number;
  opaque: GeoBuffers | null;
  cutout: GeoBuffers | null;
  translucent: GeoBuffers | null;
  /** The LOD depth this mesh was built at (echoed so the client tracks it). */
  lod: number;
}

export type MeshInbound = MeshInitMsg | MeshRequestMsg;
export type MeshOutbound = MeshResultMsg;

function geoBuffers(g: GeoBuffers | null, out: ArrayBuffer[]): void {
  if (!g) return;
  out.push(
    g.positions.buffer as ArrayBuffer,
    g.normals.buffer as ArrayBuffer,
    g.uvs.buffer as ArrayBuffer,
    g.colors.buffer as ArrayBuffer,
    g.indices.buffer as ArrayBuffer,
  );
}

export function meshRequestTransferables(msg: MeshRequestMsg): ArrayBuffer[] {
  const out = payloadTransferables(msg.self);
  for (const n of msg.neighbors) if (n) out.push(...payloadTransferables(n));
  return out;
}

export function meshResultTransferables(msg: MeshResultMsg): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  geoBuffers(msg.opaque, out);
  geoBuffers(msg.cutout, out);
  geoBuffers(msg.translucent, out);
  return out;
}

export function chunkToPayload(chunk: Chunk): ChunkPayload {
  const sections: { sy: number; states: Uint16Array }[] = [];
  const skyLight: Uint8Array[] = [];
  const blockLight: Uint8Array[] = [];
  for (let sy = 0; sy < chunk.sections.length; sy++) {
    const sec = chunk.sections[sy];
    if (!sec) continue; // sparse (TAL-2): present sections only
    sections.push({ sy, states: sec.toStates() });
    // Copy (not the live buffer): this may serialize a live main-thread chunk
    // whose light must survive the transfer intact (mesh-worker path).
    skyLight.push(Uint8Array.from(sec.skyLight));
    blockLight.push(Uint8Array.from(sec.blockLight));
  }
  return {
    cx: chunk.cx,
    cz: chunk.cz,
    range: chunk.range,
    sections,
    skyLight,
    blockLight,
    worldSurface: Int16Array.from(chunk.heightmaps.worldSurface),
    motionBlocking: Int16Array.from(chunk.heightmaps.motionBlocking),
  };
}

export function payloadToChunk(payload: ChunkPayload): Chunk {
  const chunk = new Chunk(payload.cx, payload.cz, payload.range ?? DEFAULT_RANGE);
  for (let i = 0; i < payload.sections.length; i++) {
    const { sy, states } = payload.sections[i]!;
    if (sy < 0 || sy >= chunk.sections.length || states.length !== SECTION_VOLUME) continue;
    const sec = Section.fromStates(states);
    const sky = payload.skyLight[i];
    const blk = payload.blockLight[i];
    if (sky && sky.length === SECTION_VOLUME) sec.skyLight.set(sky);
    if (blk && blk.length === SECTION_VOLUME) sec.blockLight.set(blk);
    chunk.sections[sy] = sec;
  }
  chunk.heightmaps.worldSurface.set(payload.worldSurface);
  chunk.heightmaps.motionBlocking.set(payload.motionBlocking);
  chunk.status = 'full';
  return chunk;
}

/** ArrayBuffers to hand to postMessage as transferables (zero-copy) — one per
 *  present section × 3 channels + the two heightmaps. Payload arrays are always
 *  ArrayBuffer-backed, so the narrowing cast is safe. */
export function payloadTransferables(payload: ChunkPayload): ArrayBuffer[] {
  const out: ArrayBuffer[] = [
    payload.worldSurface.buffer as ArrayBuffer,
    payload.motionBlocking.buffer as ArrayBuffer,
  ];
  for (const s of payload.sections) out.push(s.states.buffer as ArrayBuffer);
  for (const s of payload.skyLight) out.push(s.buffer as ArrayBuffer);
  for (const s of payload.blockLight) out.push(s.buffer as ArrayBuffer);
  return out;
}
